import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { dedupeBy } from '../lib/collections';
import { STOREFRONT_PRODUCT_PAGE_SIZE } from '../lib/constants';
import { sanitizeStorefrontCustomProductCollectionRef } from '../lib/storefront-engine/custom-product-collections';
import { isPublicMediaReference } from '../lib/storefront-engine/public-media-reference';
import { getDefaultStorefrontThemeSettings } from '../lib/storefront-engine/theme';
import { mapStorefrontProductError } from '../services/storefront-product.service';
import {
  resolvePublicCustomProductCollectionPage,
  resolveStorefrontPublicContentRefs,
} from '../services/storefront-public-content-resolver.service';
import type { SupabaseClient } from '../supabase/client';
import type { PublicProductCardWithUrl, StoreHomeMerchandisingErrorKey } from '../types/product';
import type {
  StorefrontCustomProductCollectionRef,
  StorefrontPublicCustomProductCollectionPage,
  StorefrontRenderSafePageConfig,
  StorefrontResolvedPublicContentRefs,
  StorefrontThemeSettings,
} from '../types/storefront-engine';

type UnknownRecord = Record<string, unknown>;
type HeroScheduleForFingerprint = {
  readonly startsAt: string | null;
  readonly endsAt: string | null;
};

const HERO_SCHEDULE_KEYS = ['startsAt', 'endsAt'] as const;
const SAFE_HERO_ID_RE = /^[a-z0-9](?:[a-z0-9_-]{0,38}[a-z0-9])?$/;
const UTC_ISO_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const INVALID_HERO_SCHEDULE = Symbol('invalid-hero-schedule');

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function sanitizeUtcIsoTimestamp(value: unknown): string | null {
  if (typeof value !== 'string' || !UTC_ISO_TIMESTAMP_RE.test(value)) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return new Date(timestamp).toISOString() === value ? value : null;
}

function sanitizeHeroScheduleForFingerprint(
  value: unknown,
): HeroScheduleForFingerprint | null | typeof INVALID_HERO_SCHEDULE {
  if (value === undefined || value === null) return null;
  if (!isRecord(value) || !hasExactKeys(value, HERO_SCHEDULE_KEYS)) {
    return INVALID_HERO_SCHEDULE;
  }
  const startsAt = value.startsAt === null ? null : sanitizeUtcIsoTimestamp(value.startsAt);
  const endsAt = value.endsAt === null ? null : sanitizeUtcIsoTimestamp(value.endsAt);
  if (value.startsAt !== null && startsAt === null) return INVALID_HERO_SCHEDULE;
  if (value.endsAt !== null && endsAt === null) return INVALID_HERO_SCHEDULE;
  if (startsAt !== null && endsAt !== null && Date.parse(endsAt) <= Date.parse(startsAt)) {
    return INVALID_HERO_SCHEDULE;
  }
  return { startsAt, endsAt };
}

function heroScheduleIsRenderableForFingerprint(slide: UnknownRecord): boolean {
  const schedule = sanitizeHeroScheduleForFingerprint(slide.schedule);
  if (schedule === INVALID_HERO_SCHEDULE) return false;
  if (!schedule) return true;
  const now = Date.now();
  if (schedule.startsAt && now < Date.parse(schedule.startsAt)) return false;
  if (schedule.endsAt && now >= Date.parse(schedule.endsAt)) return false;
  return true;
}

function getPublicHeroMediaRefs(content: unknown): readonly string[] {
  if (!isRecord(content) || !isRecord(content.hero)) return [];
  const slides =
    isRecord(content.hero) && Array.isArray(content.hero.slides) ? content.hero.slides : [];

  return slides.flatMap((slideInput) => {
    if (!isRecord(slideInput)) return [];
    const slideId = slideInput.id;
    if (typeof slideId !== 'string' || !SAFE_HERO_ID_RE.test(slideId)) return [];
    if (slideInput.enabled === false || !heroScheduleIsRenderableForFingerprint(slideInput)) {
      return [];
    }
    const media = isRecord(slideInput.media) ? slideInput.media : {};
    return (['desktop', 'mobile'] as const).flatMap((slot) => {
      const mediaReference = media[slot];
      return isPublicMediaReference(mediaReference)
        ? [`hero:${slideId}:${slot}:${mediaReference.role}:${mediaReference.mediaAssetId}`]
        : [];
    });
  });
}

export const storefrontPublicContentResolverKeys = {
  all: ['storefront-public-content-picker'] as const,
  resolvedRefs: (storeSlug: string, configFingerprint: string) =>
    [
      'storefront-public-content-picker',
      'public',
      'resolved-refs',
      storeSlug,
      configFingerprint,
    ] as const,
  customCollectionPage: (storeSlug: string, collectionRef: StorefrontCustomProductCollectionRef) =>
    [
      'storefront-public-content-picker',
      'public',
      'custom-product-collection-page',
      storeSlug,
      collectionRef,
    ] as const,
};

export function createStorefrontPublicContentRefsFingerprint(
  config: StorefrontRenderSafePageConfig,
): string {
  const refs = config.sections.flatMap((section) => {
    const dataSource = section.dataSource;
    if (section.type === 'products.rail' && dataSource?.kind === 'manual-products') {
      return [
        `${section.id}:products:${dataSource.productIds.join(',')}:${dataSource.limit ?? ''}`,
      ];
    }
    if (section.type === 'products.rail' && dataSource?.kind === 'custom-product-collection') {
      return [
        `${section.id}:custom-product-collection:${dataSource.collectionRef}:${dataSource.limit ?? ''}`,
      ];
    }
    if (section.type === 'categories.rail' && dataSource?.kind === 'manual-categories') {
      return [
        `${section.id}:categories:${dataSource.categoryIds.join(',')}:${dataSource.limit ?? ''}`,
      ];
    }
    if (section.type === 'promotions.adsSlot' && dataSource?.kind === 'manual-promotional-ads') {
      return [
        `${section.id}:promotional-ads:${dataSource.adIds.join(',')}:${dataSource.limit ?? ''}`,
      ];
    }
    const content = 'config' in section ? section.config?.content : null;
    if (section.type === 'identity.hero') {
      return getPublicHeroMediaRefs(content).map((ref) => `${section.id}:${ref}`);
    }
    const mediaReference = content && 'image' in content ? content.image : null;
    if (isPublicMediaReference(mediaReference)) {
      return [`${section.id}:media:${mediaReference.role}:${mediaReference.mediaAssetId}`];
    }
    return [];
  });
  return refs.length > 0 ? refs.sort().join('|') : 'none';
}

export function useStorefrontResolvedPublicContentRefs(
  client: SupabaseClient,
  args: {
    readonly storeSlug: string | null;
    readonly config: StorefrontRenderSafePageConfig | null;
    readonly supabaseUrl: string;
    readonly enabled?: boolean;
  },
): UseQueryResult<StorefrontResolvedPublicContentRefs> {
  const storeSlug = args.storeSlug?.trim() ?? '';
  const fingerprint = args.config
    ? createStorefrontPublicContentRefsFingerprint(args.config)
    : 'pending';
  return useQuery({
    queryKey: storeSlug
      ? storefrontPublicContentResolverKeys.resolvedRefs(storeSlug, fingerprint)
      : storefrontPublicContentResolverKeys.resolvedRefs('pending', fingerprint),
    enabled:
      storeSlug !== '' && args.config !== null && fingerprint !== 'none' && args.enabled !== false,
    queryFn: () =>
      resolveStorefrontPublicContentRefs(client, {
        storeSlug,
        config: args.config as StorefrontRenderSafePageConfig,
        supabaseUrl: args.supabaseUrl,
      }),
    staleTime: 30_000,
  });
}

export interface UseStorefrontCustomProductCollectionPageOptions {
  readonly client: SupabaseClient;
  readonly storeSlug: string;
  readonly collectionRef: string;
  readonly supabaseUrl: string;
  readonly enabled?: boolean;
}

export interface UseStorefrontCustomProductCollectionPageResult {
  readonly collection: StorefrontPublicCustomProductCollectionPage['collection'] | null;
  readonly themeSettings: StorefrontThemeSettings;
  readonly items: PublicProductCardWithUrl[];
  readonly hasMore: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly errorKey: StoreHomeMerchandisingErrorKey | null;
  readonly isFetchingNextPage: boolean;
  readonly fetchNextPage: () => void;
  readonly refetch: () => void;
}

function toMerchandisingErrorKey(error: unknown): StoreHomeMerchandisingErrorKey | null {
  if (!error) return null;
  const mapped = mapStorefrontProductError(error);
  if (mapped === 'storefront.storeHome.errors.networkFailed') {
    return 'storefront.storeHome.sections.networkError';
  }
  return 'storefront.storeHome.sections.unknownError';
}

export function useStorefrontCustomProductCollectionPage(
  options: UseStorefrontCustomProductCollectionPageOptions,
): UseStorefrontCustomProductCollectionPageResult {
  const storeSlug = options.storeSlug.trim();
  const collectionRef = sanitizeStorefrontCustomProductCollectionRef(options.collectionRef);
  const enabled = storeSlug !== '' && collectionRef !== null && options.enabled !== false;
  const query = useInfiniteQuery({
    queryKey: collectionRef
      ? storefrontPublicContentResolverKeys.customCollectionPage(storeSlug, collectionRef)
      : storefrontPublicContentResolverKeys.customCollectionPage(
          storeSlug || 'pending',
          'pc_invalid0000000000',
        ),
    enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      resolvePublicCustomProductCollectionPage(options.client, {
        storeSlug,
        collectionRef: collectionRef as StorefrontCustomProductCollectionRef,
        limit: STOREFRONT_PRODUCT_PAGE_SIZE,
        offset: pageParam as number,
        supabaseUrl: options.supabaseUrl,
      }),
    getNextPageParam: (last, _allPages, _lastPageParam, allPageParams) =>
      last?.hasMore ? allPageParams.length * STOREFRONT_PRODUCT_PAGE_SIZE : undefined,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const pages = query.data?.pages ?? [];
  const collection = pages.find((page) => page?.collection)?.collection ?? null;
  const themeSettings =
    pages.find((page) => page?.themeSettings)?.themeSettings ?? getDefaultStorefrontThemeSettings();
  const items = dedupeBy(
    pages.flatMap((page) => page?.items ?? []),
    (product) => product.id,
  );
  const hasMore = query.data?.pages[query.data.pages.length - 1]?.hasMore ?? false;

  return {
    collection,
    themeSettings,
    items,
    hasMore,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    errorKey: toMerchandisingErrorKey(query.error),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
    refetch: () => void query.refetch(),
  };
}
